import React, { useMemo, useRef, useState } from 'react';
import { VMIX_FUNCTIONS, VMIX_FUNCTION_META } from '../../lib/vmixFunctions';

// Free-text input with a filterable dropdown of vMix functions below it.
//
// Shared by the cue-action editor (VmixStepForm) and the vMix Script Editor
// tool, so an operator searches for a function the same way whether they are
// authoring a cue or a preset script.
export default function FunctionCombobox({
  value,
  onChange,
  autoFocus = true,
  className = 'w-48',
  placeholder = 'Search vMix function…',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(null); // null = show the current value, not a live filter
  const [highlight, setHighlight] = useState(0);
  const blurTimer = useRef(null);

  // When the field is being searched, filter by case-insensitive substring;
  // otherwise show the whole list so opening it is a full menu. The description
  // is searched too, so an operator who knows the effect but not the name can
  // type "rtmp" and reach StreamingSetURL. Name matches rank above description
  // matches, so an exact name is never buried.
  const filtered = useMemo(() => {
    const q = (query ?? '').trim().toLowerCase();
    if (!q) return VMIX_FUNCTIONS;
    const byName = [];
    const byDesc = [];
    for (const fn of VMIX_FUNCTIONS) {
      if (fn.toLowerCase().includes(q)) byName.push(fn);
      else if ((VMIX_FUNCTION_META.get(fn)?.desc || '').toLowerCase().includes(q)) byDesc.push(fn);
    }
    return byName.concat(byDesc);
  }, [query]);

  const shown = filtered.slice(0, 60);

  function commit(fn) {
    onChange(fn);
    setQuery(null);
    setOpen(false);
  }

  function handleKeyDown(e) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      if (open && shown[highlight]) { e.preventDefault(); e.stopPropagation(); commit(shown[highlight]); }
    } else if (e.key === 'Escape') {
      if (open) { e.stopPropagation(); setOpen(false); setQuery(null); }
    }
  }

  return (
    <div className="relative">
      <input
        type="text"
        value={query ?? value}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => { setOpen(true); setQuery(''); setHighlight(0); }}
        onBlur={() => { blurTimer.current = setTimeout(() => { setOpen(false); setQuery(null); }, 120); }}
        onKeyDown={handleKeyDown}
        autoFocus={autoFocus}
        placeholder={placeholder}
        spellCheck={false}
        className={`${className} bg-app-surface border border-app-border rounded px-2 py-1 text-xs text-app-fg focus:outline-none focus:border-blue-500`}
      />
      {open && shown.length > 0 && (
        <ul
          className="absolute z-30 mt-1 max-h-72 w-80 overflow-y-auto bg-app-surface border border-app-border rounded shadow-lg text-xs"
          onMouseDown={() => { if (blurTimer.current) clearTimeout(blurTimer.current); }}
        >
          {shown.map((fn, i) => {
            const meta = VMIX_FUNCTION_META.get(fn);
            const active = i === highlight;
            return (
              <li
                key={fn}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => commit(fn)}
                title={meta?.desc || undefined}
                className={`px-2 py-1 cursor-pointer ${active ? 'bg-blue-600 text-white' : 'text-app-fg hover:bg-app-header'}`}
              >
                <div className="flex items-baseline gap-2">
                  <span className="truncate">{fn}</span>
                  {/* Which fields this function expects — Input, Value, Duration… */}
                  {meta?.params && (
                    <span className={`ml-auto flex-shrink-0 text-[10px] ${active ? 'text-blue-100' : 'text-gray-500'}`}>
                      {meta.params}
                    </span>
                  )}
                </div>
                {meta?.desc && (
                  <div className={`truncate text-[10px] ${active ? 'text-blue-100' : 'text-gray-500'}`}>
                    {meta.desc}
                  </div>
                )}
              </li>
            );
          })}
          {filtered.length > shown.length && (
            <li className="px-2 py-1 text-gray-500 italic">+{filtered.length - shown.length} more — keep typing…</li>
          )}
        </ul>
      )}
    </div>
  );
}
